/**
 * Die Zeile „Mac“ in der Lage unter der Glocke. (29.09.2026, Ticket 2823; umgezogen 11.10.2026)
 *
 * Martin, zur Karte „Vespera Desktop“: *„Außerdem sollte nicht in der
 * Kopfzeile auch eine Information stehen oder so etwas in der Art.“*
 * Und am 11.10.2026, zum Kopf mit den vielen Punkten: raus aus dem Kopf.
 *
 * Eine ruhige Zeile: „Mac verbunden“ mit einem Punkt — grün, solange
 * Vespera Desktop verbunden ist, grau, wenn nicht. Sie steht NUR in der Lage
 * (Fenster unter der Glocke, lib/lage-fenster.ts), nicht mehr im Kopf, und
 * erscheint nur bei Konten, die je eine Mac-App gekoppelt haben (`gekoppelt`
 * aus /api/daemon-status); alle anderen sehen nichts. Antippen öffnet die
 * Einstellungen bei den Verbindungen, dort steht die Karte.
 *
 * ⚠️ Akku (Ticket 2752): höchstens jede Minute, nur bei sichtbarer Seite —
 *    `starteSichtbarenTakt` wie der Entwicklungs-Ticker (2733). Ein Konto
 *    ohne Mac fragt nach der ersten Antwort gar nicht mehr; koppelt es
 *    später, meldet die Karte das (Ereignis `vespera:desktop-status`).
 * ⚠️ EIN Besitzer für `hidden`: nur diese Datei. Die Karte schickt nur
 *    ihren Stand, sie schaltet die Zeile nie selbst. Nach jeder Änderung
 *    sagt `lageGeaendert()` der Glocke und der Tafel Bescheid.
 */
import { starteSichtbarenTakt } from "./abfrage-takt.js";
import { lageGeaendert } from "./lage-fenster.js";

/** Höchstens so oft wird gefragt (und nur bei sichtbarer Seite). */
const MAC_TAKT_MS = 60_000;

/** Das Ereignis, mit dem die Karte ihren frischen Stand weitergibt. */
export const DESKTOP_STATUS_EREIGNIS = "vespera:desktop-status";

interface MacStand {
  gekoppelt: boolean;
  verbunden: boolean;
  pausiert: boolean;
}

/** Die Antwort von /api/daemon-status als Stand — oder null, wenn sie nichts taugt. */
function macStandAus(d: unknown): MacStand | null {
  if (!d || typeof d !== "object") return null;
  const o = d as Record<string, unknown>;
  if (typeof o.connected !== "boolean") return null;
  return {
    // Ältere Server kennen `gekoppelt` nicht — dann zählt, was verbunden ist.
    gekoppelt: o.gekoppelt === true || o.connected,
    verbunden: o.connected,
    pausiert: o.paused === true,
  };
}

/** Das Wort in der Zeile: „verbunden“, „verbunden, pausiert“ oder „nicht verbunden“. */
function macStand(s: MacStand): string {
  return !s.verbunden ? "nicht verbunden" : s.pausiert ? "verbunden, pausiert" : "verbunden";
}

/** Der ganze Satz — für Vorleser und als Hinweis unter der Maus. */
function macSatz(s: MacStand): string {
  // Beginnt mit dem sichtbaren Wort „Mac“ (WCAG 2.5.3: der Name enthält, was zu lesen ist).
  return `Mac, Vespera Desktop: ${macStand(s)}. Antippen öffnet die Verbindungen.`;
}

/** Den Stand in die Zeile schreiben — sichtbar nur bei gekoppeltem Konto. */
export function zeichneMac(s: MacStand): void {
  const el = document.getElementById("topbar-mac");
  if (!el) return;
  el.hidden = !s.gekoppelt;
  if (s.gekoppelt) {
    el.toggleAttribute("data-verbunden", s.verbunden);
    const wort = document.getElementById("topbar-mac-stand");
    if (wort) wort.textContent = macStand(s);
    const satz = macSatz(s);
    el.setAttribute("aria-label", satz);
    el.title = satz;
  }
  lageGeaendert();
}

let anhalten: (() => void) | null = null;
let taktAus: (() => void) | null = null;
/** Wann zuletzt gefragt wurde (ms) — der Abstand gilt für Takt UND Rückkehr. */
let zuletzt = Number.NEGATIVE_INFINITY;

/** Nicht (mehr) fragen — der Rest (Knopf, Ereignis der Karte) bleibt. */
function taktStoppen(): void {
  taktAus?.();
  taktAus = null;
  document.removeEventListener("visibilitychange", vielleichtFragen);
}

function nimm(s: MacStand): void {
  zeichneMac(s);
  // Ohne gekoppelte App gibt es nichts zu beobachten.
  if (!s.gekoppelt) taktStoppen();
  else if (!taktAus && anhalten) taktStarten();
}

async function frage(): Promise<void> {
  try {
    const r = await fetch("/api/daemon-status", { credentials: "same-origin" });
    if (r.status === 401 || r.status === 403) {
      const el = document.getElementById("topbar-mac");
      if (el) el.hidden = true;
      lageGeaendert();
      taktStoppen();
      return;
    }
    if (!r.ok) return;
    const s = macStandAus(await r.json());
    if (s) nimm(s);
  } catch {
    /* Die Lage darf an dieser Frage nie scheitern — der alte Stand bleibt. */
  }
}

/** Fragen, wenn jemand hinsieht und die letzte Frage eine Minute her ist. */
function vielleichtFragen(): void {
  if (document.visibilityState !== "visible") return;
  const jetzt = Date.now();
  if (jetzt - zuletzt < MAC_TAKT_MS - 2_000) return;
  zuletzt = jetzt;
  void frage();
}

function taktStarten(): void {
  taktAus = starteSichtbarenTakt(vielleichtFragen, MAC_TAKT_MS);
  document.addEventListener("visibilitychange", vielleichtFragen);
}

/** Frischer Stand aus der Karte — zählt wie eine eigene Antwort. */
function ausDerKarte(ev: Event): void {
  const s = macStandAus((ev as CustomEvent<unknown>).detail);
  if (!s) return;
  zuletzt = Date.now();
  nimm(s);
}

function oeffneVerbindungen(): void {
  document.dispatchEvent(new CustomEvent("vespera:go-settings", { detail: { sub: "verbindungen" } }));
}

/**
 * Das Zeichen starten: gleich fragen (wenn sichtbar), danach im sichtbaren
 * Takt. Ein zweiter Aufruf startet nichts doppelt. Gibt den Abschalter zurück.
 */
export function starteMacZeichen(): () => void {
  if (anhalten) return anhalten;
  const el = document.getElementById("topbar-mac");
  el?.addEventListener("click", oeffneVerbindungen);
  document.addEventListener(DESKTOP_STATUS_EREIGNIS, ausDerKarte);
  taktStarten();
  const aus = (): void => {
    taktStoppen();
    el?.removeEventListener("click", oeffneVerbindungen);
    document.removeEventListener(DESKTOP_STATUS_EREIGNIS, ausDerKarte);
    if (anhalten === aus) anhalten = null;
    zuletzt = Number.NEGATIVE_INFINITY;
  };
  anhalten = aus;
  vielleichtFragen();
  return aus;
}
